"use client";

import { c as _c } from "react/compiler-runtime";
import useCreateModalState from "@/hooks/useCreateModalState";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { ReactNode } from "react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createBreakpoint } from "react-use";
import { getActiveWaveIdFromUrl } from "@/helpers/navigation.helpers";
import { markDropCloseNavigation } from "@/helpers/drop-close-navigation.helpers";
import type { ApiDrop } from "../../generated/models/ApiDrop";
import { DropSize } from "../../helpers/waves/drop.helpers";
import { useSidebarState } from "../../hooks/useSidebarState";
import { commonApiFetch } from "../../services/api/common-api";
import { useAuth } from "../auth/Auth";
import BrainDesktopDrop from "../brain/BrainDesktopDrop";
import WebBrainLeftSidebar from "../brain/left-sidebar/web/WebLeftSidebar";
import { useLayout } from "../brain/my-stream/layout/LayoutContext";
import BrainRightSidebar, { SidebarTab } from "../brain/right-sidebar/BrainRightSidebar";
import { QueryKey } from "../react-query-wrapper/ReactQueryWrapper";
import CreateWaveModal from "../waves/create-wave/CreateWaveModal";
import { WaveChatScrollProvider } from "@/contexts/wave/WaveChatScrollContext";
import { useClosingDropId } from "@/hooks/useClosingDropId";
const useBreakpoint = createBreakpoint({
  XL: 1400,
  LG: 1024,
  S: 0
});
interface WavesMessagesWrapperProps {
  readonly children: ReactNode;
  readonly defaultPath?: string | undefined; // "/waves" or "/messages"
  readonly showLeftSidebar?: boolean | undefined;
}
const WavesMessagesWrapper: React.FC<WavesMessagesWrapperProps> = t0 => {
  const $ = _c(56);
  const {
    children,
    defaultPath: t1,
    showLeftSidebar: t2
  } = t0;
  const defaultPath = t1 === undefined ? "/waves" : t1;
  const showLeftSidebar = t2 === undefined ? true : t2;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const breakpoint = useBreakpoint();
  const {
    contentContainerStyle
  } = useLayout();
  const {
    isRightSidebarOpen,
    closeRightSidebar
  } = useSidebarState();
  const {
    connectedProfile
  } = useAuth();
  const {
    isWaveModalOpen,
    close
  } = useCreateModalState();
  const [sidebarTab, setSidebarTab] = useState(SidebarTab.ABOUT);
  let t3;
  let waveId;
  if ($[0] !== pathname || $[1] !== searchParams) {
    const rawDropId = searchParams.get("drop") ?? undefined;
    waveId = getActiveWaveIdFromUrl({
      pathname,
      searchParams
    }) ?? undefined;
    t3 = rawDropId && /^[a-zA-Z0-9-_]+$/.test(rawDropId) ? rawDropId : undefined;
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t3;
    $[3] = waveId;
  } else {
    t3 = $[2];
    waveId = $[3];
  }
  const dropId = t3;
  const {
    effectiveDropId,
    beginClosingDrop
  } = useClosingDropId(dropId);
  const isMobile = breakpoint === "S";
  const isLargeDesktop = breakpoint === "XL";
  let t4;
  let t5;
  if ($[4] !== closeRightSidebar || $[5] !== isRightSidebarOpen || $[6] !== waveId) {
    t4 = () => {
      if (!waveId && isRightSidebarOpen) {
        closeRightSidebar();
      }
    };
    t5 = [waveId, isRightSidebarOpen, closeRightSidebar];
    $[4] = closeRightSidebar;
    $[5] = isRightSidebarOpen;
    $[6] = waveId;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[9] !== effectiveDropId) {
    t6 = [QueryKey.DROP, {
      drop_id: effectiveDropId
    }];
    t7 = async () => {
      if (!effectiveDropId) {
        throw new Error("Cannot fetch drop without a drop id");
      }
      return await commonApiFetch({
        endpoint: `drops/${effectiveDropId}`
      });
    };
    $[9] = effectiveDropId;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  const t8 = !!effectiveDropId;
  let t9;
  if ($[12] !== t6 || $[13] !== t7 || $[14] !== t8) {
    t9 = {
      queryKey: t6,
      queryFn: t7,
      placeholderData: keepPreviousData,
      enabled: t8
    };
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    data: drop,
    error: dropError
  } = useQuery(t9);
  let t10;
  if ($[16] !== beginClosingDrop || $[17] !== defaultPath || $[18] !== dropId || $[19] !== pathname || $[20] !== router || $[21] !== searchParams) {
    t10 = () => {
      if (dropId) {
        beginClosingDrop(dropId);
        markDropCloseNavigation();
      }
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("drop");
      const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname || defaultPath;
      router.replace(newUrl, {
        scroll: false
      });
    };
    $[16] = beginClosingDrop;
    $[17] = defaultPath;
    $[18] = dropId;
    $[19] = pathname;
    $[20] = router;
    $[21] = searchParams;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const onDropClose = t10;
  drop?.id;
  let t11;
  if ($[23] !== drop?.id || $[24] !== effectiveDropId) {
    t11 = effectiveDropId && drop?.id.toLowerCase() === effectiveDropId.toLowerCase();
    $[23] = drop?.id;
    $[24] = effectiveDropId;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const isDropOpen = Boolean(t11);
  const shouldShowLeftSidebar = showLeftSidebar && (!isMobile || !waveId);
  const shouldShowMainContent = !isMobile || waveId !== undefined;
  const shouldShowDropOverlay = isDropOpen && drop !== undefined && shouldShowMainContent;
  const shouldShowRightSidebar = Boolean(isRightSidebarOpen && waveId && !isDropOpen);
  const canInlineRight = !isMobile && (isLargeDesktop || breakpoint === "LG");
  let rightVariant = null;
  if (shouldShowRightSidebar) {
    rightVariant = canInlineRight ? "inline" : "overlay";
  }
  if (dropError && effectiveDropId) {
    console.error("Failed to load drop:", dropError);
  }
  let t12;
  if ($[26] !== rightVariant || $[27] !== shouldShowLeftSidebar) {
    t12 = shouldShowLeftSidebar && <WebBrainLeftSidebar isCollapsed={rightVariant === "inline"} />;
    $[26] = rightVariant;
    $[27] = shouldShowLeftSidebar;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== children || $[30] !== drop || $[31] !== onDropClose || $[32] !== shouldShowDropOverlay || $[33] !== shouldShowMainContent) {
    t13 = shouldShowMainContent && <div className="tw-flex tw-h-full tw-min-w-0 tw-flex-grow tw-flex-col tw-border-y-0 tw-border-l-0 tw-border-r tw-border-solid tw-border-iron-800">{children}{shouldShowDropOverlay && <div className="tw-fixed tw-inset-y-0 tw-left-[var(--left-rail,0px)] tw-right-0 tw-z-[1010] lg:tw-absolute lg:tw-inset-0 lg:tw-z-[1010]"><BrainDesktopDrop drop={{
          type: DropSize.FULL,
          ...drop,
          stableKey: drop.id,
          stableHash: drop.id
        }} onClose={onDropClose} /></div>}</div>;
    $[29] = children;
    $[30] = drop;
    $[31] = onDropClose;
    $[32] = shouldShowDropOverlay;
    $[33] = shouldShowMainContent;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== rightVariant || $[36] !== sidebarTab || $[37] !== waveId) {
    t14 = rightVariant === "inline" && <div className="tw-hidden tw-flex-shrink-0 tw-pl-6 tw-pt-2 lg:tw-block"><BrainRightSidebar variant="inline" waveId={waveId} activeTab={sidebarTab} setActiveTab={setSidebarTab} /></div>;
    $[35] = rightVariant;
    $[36] = sidebarTab;
    $[37] = waveId;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== contentContainerStyle || $[40] !== t12 || $[41] !== t13 || $[42] !== t14) {
    t15 = <div className="tw-relative tw-flex tw-flex-col"><div className="tw-relative tw-flex tw-flex-grow"><div className="tw-relative tw-mx-auto tw-flex tw-w-full tw-max-w-full tw-flex-grow"><div className="tw-relative tw-flex tw-w-full tw-overflow-hidden" style={contentContainerStyle}>{t12}{t13}{t14}</div></div></div></div>;
    $[39] = contentContainerStyle;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== rightVariant || $[45] !== sidebarTab || $[46] !== waveId) {
    t16 = rightVariant === "overlay" && <BrainRightSidebar variant="overlay" waveId={waveId} activeTab={sidebarTab} setActiveTab={setSidebarTab} />;
    $[44] = rightVariant;
    $[45] = sidebarTab;
    $[46] = waveId;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== close || $[49] !== connectedProfile || $[50] !== isWaveModalOpen) {
    t17 = connectedProfile && <CreateWaveModal isOpen={isWaveModalOpen} onClose={close} profile={connectedProfile} />;
    $[48] = close;
    $[49] = connectedProfile;
    $[50] = isWaveModalOpen;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== t15 || $[53] !== t16 || $[54] !== t17) {
    t18 = <WaveChatScrollProvider>{t15}{t16}{t17}</WaveChatScrollProvider>;
    $[52] = t15;
    $[53] = t16;
    $[54] = t17;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  return t18;
};
export default WavesMessagesWrapper;